import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  saveManyUsersModules,
  saveUserModules,
  saveUserReports,
  userModulesQuery,
  userReportsQuery,
  usersReportsShellQuery,
  usersReportsTableQuery,
  type ModuleForm,
  type ReportModule,
  type UserModuleRow,
  type UsersReportsShell,
} from '../../api/settingsCompanyReports'
import { DataTable, useTableState, type TableColumn } from '../../ui/DataTable'
import { Forbidden } from '../../ui/Forbidden'
import { LoadFailed } from '../../ui/LoadFailed'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'
import { CompanyReportsFrame } from './CompanyReportsPage'
import './UsersReportsPage.css'

/**
 * Settings -> Reports -> Users Reports
 * (`settings/company_reports/users_settings_report`).
 *
 * MACHETELE VECHI, element cu element:
 *  - `users_settings/index.php`: antetul, widgetul de selectie multipla si
 *    tabelul `#usersTable` (coloanele din `usersModel`,
 *    `public/js/settings/company_reports.js`);
 *  - `users_settings/actionTabs.php`: cele doua file ale randului desfacut,
 *    "Company reports" si "Other reports";
 *  - `users_settings/others/companyReportsSettings.php`: tabelul cu bifele pe
 *    module (View / Email / Alarms / SNMP Alarms / Daily-Weekly-Monthly / ora);
 *  - `users_settings/others/otherReportsSettings.php`: tabelul cu celelalte
 *    rapoarte (Email / ora);
 *  - `users_settings/multiple_select/*.php`: acelasi tabel de module, o data
 *    pentru toti oamenii bifati.
 *
 * CE HOTARASTE SERVERUL. Macheta veche desena lista numai furnizorului si
 * numai daca firma are rapoartele pornite; altfel un avertisment (cu legatura
 * spre Customers) sau "Access denied". Starea vine gata hotarata in
 * `shell.state`, iar ecranul doar o deseneaza.
 *
 * ORA. `wickedpicker` scria `HH : MM` intr-un `<input type="text">`; aici e
 * un `<input type="time">`, care da `HH:MM`. Serverul primeste si intoarce
 * ora LOCALA a omului logat, ca in portal.
 *
 * REGULA DIN JS-UL VECHI (`companyReportsSettings-1.js`): bifa de "Email
 * Report" aprinde singura "View Report" din stanga ei si deblocheaza
 * "Include Alarms" din dreapta; stinsa, stinge si blocheaza alarmele. La fel
 * pentru bifele de frecventa (`.email`): fiecare aprinde bifa din stanga.
 * Se pastreaza, fiindca asa se poarta formularul pe care il stie omul.
 */

const MODULE_COLUMNS = [
  ['inventoree', 'Inventoree'],
  ['proactivitee', 'Proactivitee'],
  ['printeeng', 'Printeeng'],
  ['securitee', 'Securitee'],
  ['productivitee', 'Productivitee'],
] as const

/** ID-ul modulului Proactivitee: singurul cu "SNMP Alarms" in loc de "Alarms" (macheta: `module_id == 3`). */
const PROACTIVITEE = 3

/**
 * Cele trei frecvente, cu eticheta si lamurirea lor - o singura data, fiindca
 * antetul si randul trebuie sa arate aceleasi trei coloane, in aceeasi ordine.
 */
const FREQUENCY = [
  ['daily', 'Daily', 'If you choose Daily option, you will receive the email every day.'],
  ['weekly', 'Weekly', 'If you choose Weekly option, you will receive the email every Monday.'],
  ['monthly', 'Monthly', 'If you choose Monthly option, you will receive the email on the 1st of the month.'],
] as const

function esc(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function failed(error: unknown): string {
  return error instanceof ApiError && error.message ? error.message : 'Something went wrong.'
}

/**
 * CE ARATA O FILA A RANDULUI DESFACUT CAND N-ARE DATE.
 *
 * Amandoua filele aveau aceeasi garda: `if (data.error) return <div
 * className="alert alert-danger">{failed(error)}</div>`. Un 403, un 404, un
 * 500 si o retea cazuta ieseau TOATE la fel - un dreptunghi rosu cu o
 * propozitie - si niciuna nu se putea reincerca. Iar "Not found.", raspunsul
 * pe care capetele il dau cand omul nu e al firmei alese
 * (`UsersReports::userModules` -> `isUserOfCompany`), nu se deosebea cu nimic
 * de o cerere picata: cine il citea nu putea sti daca sa reincerce sau sa
 * inchida randul.
 *
 * Acum fiecare stare spune ce e: lipsa dreptului (403), omul care nu e al
 * firmei (404), si orice altceva - o cadere, cu motivul si cu Retry.
 */
function PanelState({ error, onRetry, what }: { error: unknown; onRetry: () => void; what: string }) {
  if (error instanceof ApiError && error.status === 403) {
    return (
      <div className="alert alert-danger od-panel-denied" role="alert">
        <i className="fa fa-ban" aria-hidden="true"></i> No access rights.
      </div>
    )
  }

  /* 404 NU e o cadere: capatul a raspuns, si raspunsul e ca omul asta nu e al
     firmei alese. Reincercarea n-ar schimba nimic, deci nu se ofera. */
  if (errorCode(error) === 'not_found') {
    return (
      <div className="alert alert-warning od-panel-missing" role="alert">
        <i className="fa fa-ban" aria-hidden="true"></i> This user is not in the selected company any more, so {what.toLowerCase()}{' '}
        cannot be shown. Close the row and refresh the list.
      </div>
    )
  }

  /* ORICE ALT 404 NU stie nimic despre firma omului.
     Un 404 pe care il da RUTA - adresa iese stramba, `/users/NaN/modules`, si
     `requirements: ['id' => '\d+']` nu o mai prinde - nu e un raspuns despre
     om, e o cerere gresita plecata de aici. Spus cu textul de deasupra, ar
     trimite omul sa caute in Customers o mutare care nu s-a intamplat. */
  if (error instanceof ApiError && error.status === 404) {
    return (
      <div className="alert alert-warning od-panel-lost" role="alert">
        <i className="fa fa-ban" aria-hidden="true"></i> {what} could not be opened for this row. Close the row and refresh the
        list; if it happens again, the fault is in this screen.
      </div>
    )
  }

  return <LoadFailed what={what} error={error} onRetry={onRetry} />
}

/** Capatul a raspuns, si raspunsul e GOL - nu o cadere, si nu un om lipsa. */
function PanelEmpty({ children }: { children: React.ReactNode }) {
  return (
    <div className="alert alert-primary od-panel-empty" role="status">
      <i className="fa fa-ban" aria-hidden="true"></i> {children}
    </div>
  )
}

/**
 * Acelasi inteles, la SALVARE. Butonul Save bate pe aceleasi adrese, deci se
 * loveste de aceleasi doua porti; toastul spunea insa doar "Not found.", care
 * despre un buton de salvare nu inseamna nimic.
 */
function saveFailed(error: unknown): string {
  if (error instanceof ApiError && error.status === 403) return 'No access rights.'
  if (errorCode(error) === 'not_found') return 'This user is not in the selected company any more.'
  if (error instanceof ApiError && error.status === 404) return 'This user could not be saved from this row. Refresh the list and try again.'

  return failed(error)
}

/**
 * CODUL pe care capatul l-a SPUS, nu numarul de pe raspuns.
 *
 * `{"error":"not_found"}` il scrie `SettingsCompanyReportsUsersController::ok`
 * cand `isUserOfCompany` spune nu - ALA e raspunsul despre om. Un 404 venit de
 * la ruta (adresa stramba, `\d+` nepotrivit) n-are corpul asta, si nu spune
 * nimic despre firma nimanui.
 */
function errorCode(error: unknown): string {
  if (!(error instanceof ApiError) || error.status !== 404) return ''
  const code = error.details?.error

  return typeof code === 'string' ? code : ''
}

/**
 * OMUL DIN RAND, sau `null` cand randul n-a venit cu el.
 *
 * `Number(undefined)` e `NaN`, si `NaN` nu se vede: adresa iese
 * `/users/NaN/modules`, ruta o refuza cu 404, si pana acum ecranul citea 404-ul
 * ala ca pe "omul a plecat din firma". Cand `ID` lipseste dar `DT_RowId` e
 * acolo (`row_<ID>`, scris de `UsersReports::users`), omul se mai stie din el.
 */
export function userIdOf(row: Record<string, unknown>): number | null {
  const direct = Number(row.ID)
  if (Number.isInteger(direct) && direct > 0) return direct

  const marked = /^row_(\d+)$/.exec(String(row.DT_RowId ?? ''))
  const fromMark = marked ? Number(marked[1]) : 0

  return fromMark > 0 ? fromMark : null
}

/**
 * ID-UL RANDULUI, DIFERIT DE AL ORICARUI ALT RAND.
 *
 * `DataTable` deschide randul al carui id se potriveste cu al randului apasat
 * (`openRow === props.rowId(row)`), deci doua randuri cu acelasi id se deschid
 * AMANDOUA. `row_${Number(row.ID)}` dadea `row_NaN` la fiecare rand caruia ii
 * lipsea `ID`: un click ii desfacea pe toti deodata. Randul fara om isi
 * primeste aici id-ul din locul lui in lista, ca sa ramana singur.
 */
export function rowIdOf(row: Record<string, unknown>, index: number): string {
  const id = userIdOf(row)

  return id === null ? `row_missing_${index}` : `row_${id}`
}

/**
 * RANDUL CARE N-A VENIT CU OMUL LUI.
 *
 * Nu se cere nimic de pe server: fara id, adresa ar iesi stramba si raspunsul
 * ar fi un 404 care nu spune nimic. Textul spune ce e - o lipsa din ecran, nu
 * o mutare a omului in alta firma.
 */
function PanelNoId({ what }: { what: string }) {
  return (
    <div className="alert alert-warning od-panel-no-id" role="alert">
      <i className="fa fa-ban" aria-hidden="true"></i> This row arrived without a user id, so {what.toLowerCase()} cannot be
      shown. Close the row and refresh the list; if it happens again, the fault is in this screen.
    </div>
  )
}

/** Coloanele din `usersModel`, in aceeasi ordine, cu aceleasi insigne. */
export function usersReportsColumns(): TableColumn[] {
  const columns: TableColumn[] = [
    {
      index: 0,
      title: '',
      orderable: false,
      visible: true,
      width: 50,
      /**
       * FARA OM, FARA BUTON DE DESFACERE.
       *
       * `DataTable` desface randul de sub `center.show_details`. Un rand caruia
       * nu i se stie omul n-are ce deschide: un plus care scoate un panou de
       * scuze e o minciuna de ecran, si tot el cerea pana acum
       * `/users/0/modules`. Randurile celorlalti raman neatinse.
       */
      html: (row) => {
        const id = userIdOf(row)

        return id === null
          ? '<center class="color-light-grey font-16" title="This row arrived without a user id"><i class="fas fa-minus-circle"></i></center>'
          : `<center class="show_details color-dark-grey pointer font-16" data-id="${id}"><i class="fas fa-plus-circle"></i></center>`
      },
    },
    {
      index: 1,
      title: 'User name',
      orderable: true,
      visible: true,
      html: (row) => (row.Nume ? `${esc(row.Prenume)} ${esc(row.Nume)}` : '<i class="fas fa-minus font-10"></i>'),
    },
  ]
  MODULE_COLUMNS.forEach(([key, label], i) => {
    columns.push({
      index: 2 + i,
      title: label,
      orderable: false,
      visible: true,
      html: (row) =>
        Number(row[`${key}_email`]) > 0
          ? `<i class="far fa-check-circle font-18 color-green2" title="${label} report is ENABLED" data-placement="top" data-toggle="tooltip"></i>`
          : `<i class="far fa-dot-circle font-18 color-light-grey" title="${label} report is DISABLED" data-placement="top" data-toggle="tooltip"></i>`,
    })
  })
  columns.push({
    index: 7,
    title: '',
    titleHtml: '<center><input type="checkbox" class="allDevices"/></center>',
    orderable: false,
    visible: true,
    width: 50,
    /* Nici bifa: un rand fara om n-are ce trimite in `users[]`. */
    html: (row) => {
      const id = userIdOf(row)

      return id === null
        ? '<center></center>'
        : `<center><input value="${id}" type="checkbox" class="oneDevice"${row.__checked ? ' checked' : ''}/></center>`
    },
  })
  return columns
}

/** Casuta desenata ca la iCheck (`icheckbox_square-blue` / `-red`), cu inputul adevarat drept tinta. */
function ICheck({
  checked,
  disabled,
  color,
  label,
  onChange,
}: {
  checked: boolean
  disabled?: boolean
  color: 'blue' | 'red'
  label: string
  onChange(on: boolean): void
}) {
  return (
    <div
      className={`icheckbox_square-${color}${checked ? ' checked' : ''}${disabled ? ' disabled' : ''}`}
      style={{ position: 'relative', display: 'inline-block', verticalAlign: 'middle' }}
    >
      <input
        type="checkbox"
        aria-label={label}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, margin: 0, cursor: disabled ? 'default' : 'pointer' }}
      />
    </div>
  )
}

/**
 * ORA, CU UN CEAS CARE CHIAR DESCHIDE CEVA.
 *
 * Macheta veche punea `<div class="input-group bootstrap-timepicker
 * timepicker">` si un `<i class="glyphicon glyphicon-time">`, iar alegatorul
 * il deschidea `wickedpicker` din `companyReportsSettings-1.js`. In v4 nu se
 * incarca nici wickedpicker, nici bootstrap-timepicker: clasele ramasesera
 * marcaj mort, iar ceasul - un patrat gol.
 *
 * DOUA LUCRURI, AMANDOUA MASURATE, NU BANUITE:
 *
 *  1. `glyphicon-time` nu se poate desena. `app.css` are `@font-face` pentru
 *     "Glyphicons Halflings", dar fisierul pe care il cere -
 *     `fonts/vendor/bootstrap/dist/glyphicons-halflings-regular.woff2` - NU e
 *     langa aplicatie: sub `public/fonts/vendor/` sunt doar @fortawesome,
 *     boxicons si simple-line-icons. Fara fisier, orice glyphicon iese
 *     dreptunghiul gol. `far fa-clock` se desenaza: `app.css` scrie
 *     `.fa-clock:before{content:"\f017"}`, `.far{font-weight:400}`, iar
 *     `webfa-regular-400.woff2` chiar e in `public/fonts/vendor/`. Fila
 *     "Other reports" o scria deja asa - aici s-a potrivit cu ea.
 *     (`fa-clock-o` e nume de Font Awesome 4 si NU exista in 5.15: cautat in
 *     foaia servita, zero potriviri.)
 *
 *  2. Ceasul nu era apasabil. Un `<span>` langa camp nu face nimic, iar
 *     `<input type="time">` isi deschide alegatorul propriu numai de pe
 *     butonasul lui din coltul din dreapta. Acum ceasul e `<button>` si cheama
 *     `showPicker()` - alegatorul browserului, nu un plugin adus inapoi. Unde
 *     `showPicker` nu exista, focusul lasa omul sa scrie ora, ca pana acum.
 *
 * CE SE SALVEAZA NU SE SCHIMBA: tot `<input type="time">`, deci tot `HH:MM`
 * local, trimis in `module[<id>][email_time]` / `reports[<id>][email_time]`,
 * exact ce asteapta `UsersReports::toGmt` (`/^\d{1,2}:\d{2}$/`).
 */
function TimeField({
  label,
  value,
  onChange,
  className,
  style,
}: {
  label: string
  value: string
  onChange(value: string): void
  className?: string
  style?: React.CSSProperties
}) {
  const input = useRef<HTMLInputElement>(null)

  return (
    <div className="input-group urp-time">
      <input
        ref={input}
        type="time"
        aria-label={label}
        className={className}
        style={style}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <button
        type="button"
        className="input-group-addon urp-time-open"
        aria-label={`${label}: pick the time`}
        onClick={() => {
          const el = input.current
          if (!el) return
          el.focus()
          if (typeof el.showPicker === 'function') el.showPicker()
        }}
      >
        <i className="far fa-clock" aria-hidden="true"></i>
      </button>
    </div>
  )
}

const EMPTY_FORM: ModuleForm = {
  view_report: false,
  email_report: false,
  daily: false,
  weekly: false,
  monthly: false,
  enable_alarms: false,
  email_time: '09:00',
}

function formOf(row: UserModuleRow): ModuleForm {
  return {
    view_report: row.view_report === 1,
    email_report: row.email_report === 1,
    daily: row.daily === 1,
    weekly: row.weekly === 1,
    monthly: row.monthly === 1,
    enable_alarms: row.enable_alarms === 1,
    email_time: row.email_time,
  }
}

/**
 * Tabelul cu bifele pe module - `companyReportsSettings.php` si
 * `multipleCompanyReportsSettings.php` sunt acelasi tabel, cu alte valori de
 * pornire si alt buton.
 */
function ModulesForm({
  modules,
  initial,
  onSave,
  busy,
}: {
  modules: { id: number; name: string }[]
  initial: Record<number, ModuleForm>
  onSave(form: Record<number, ModuleForm>): void
  busy: boolean
}) {
  const [form, setForm] = useState<Record<number, ModuleForm>>(initial)

  useEffect(() => setForm(initial), [initial])

  const set = (id: number, patch: Partial<ModuleForm>) =>
    setForm((prev) => ({ ...prev, [id]: { ...(prev[id] ?? EMPTY_FORM), ...patch } }))

  /* `input.email` bifat aprinde bifa din stanga si deblocheaza alarmele; stins, le stinge. */
  const setEmail = (id: number, on: boolean) =>
    set(id, on ? { email_report: true, view_report: true } : { email_report: false, enable_alarms: false })

  const cell: React.CSSProperties = { textAlign: 'center', background: '#b3b3b3' }
  const head: React.CSSProperties = { width: 200, textAlign: 'center' }
  const info = (title: string) => (
    <h5>
      <i className="fas fa-info-circle color-blue font-14" style={{ cursor: 'default' }} data-toggle="tooltip" data-placement="top" title={title}>
        {' '}
      </i>
    </h5>
  )

  return (
    <form
      className="form-horizontal labelcustomize userSettings"
      onSubmit={(e) => {
        e.preventDefault()
        onSave(form)
      }}
    >
      <div className="col-md-12 divider" style={{ marginTop: 0 }}>
        <h4 className="bordered">
          <strong>Enable report</strong> details
        </h4>
      </div>

      <table className="table table-hover">
        <thead>
          <tr>
            <th className="a-left" style={{ width: 200 }}>
              Module
            </th>
            <th className="a-left" style={head}>
              View Report
            </th>
            <th className="a-left" style={head}>
              Email Report
            </th>
            <th className="a-left" style={head}>
              Include Alarms in Report
            </th>
            <th className="a-left" style={head}>
              Include SNMP Alarms in Report
            </th>
            <th className="align-center" style={head}>
              Report frequency
              <div className="urp-freq">
                {FREQUENCY.map(([flag, label, hint]) => (
                  <span key={flag} className="urp-freq-label">
                    {label}
                    {info(hint)}
                  </span>
                ))}
              </div>
            </th>
            <th className="align-center" style={head}>
              Report time{info('The time of day when you will receive the report.')}
            </th>
          </tr>
        </thead>
        <tbody>
          {modules.map((module) => {
            const m = form[module.id] ?? EMPTY_FORM
            const alarms = (
              <ICheck
                color="red"
                label={`${module.name}: include alarms`}
                checked={m.enable_alarms}
                disabled={!m.email_report}
                onChange={(on) => set(module.id, { enable_alarms: on })}
              />
            )
            const frequency = FREQUENCY.map(([flag]) => (
              <span key={flag} className="urp-freq-slot">
                <ICheck
                  color="blue"
                  label={`${module.name}: ${flag}`}
                  checked={m[flag]}
                  onChange={(on) => set(module.id, { [flag]: on })}
                />
              </span>
            ))
            return (
              <tr key={module.id}>
                <td className="a-left" scope="row">
                  <strong>{module.name}</strong>
                </td>
                <td style={cell} className="a-left">
                  <ICheck
                    color="blue"
                    label={`${module.name}: view report`}
                    checked={m.view_report}
                    onChange={(on) => set(module.id, { view_report: on })}
                  />
                </td>
                <td style={cell} className="a-left">
                  <ICheck
                    color="blue"
                    label={`${module.name}: email report`}
                    checked={m.email_report}
                    onChange={(on) => setEmail(module.id, on)}
                  />
                </td>
                {module.id === PROACTIVITEE ? (
                  <>
                    <td style={cell} className="a-left"></td>
                    <td style={cell} className="a-left">
                      {alarms}
                    </td>
                  </>
                ) : (
                  <>
                    <td style={cell} className="a-left">
                      {alarms}
                    </td>
                    <td style={cell} className="a-left"></td>
                  </>
                )}
                <td style={cell} className="a-left">
                  <div className="urp-freq">{frequency}</div>
                </td>
                <td style={cell} className="align-center">
                  <div style={{ width: '60%', marginLeft: '25%' }} className="align-center">
                    <div className="form-group">
                      <div className="col-sm-20">
                        <TimeField
                          label={`${module.name}: report time`}
                          value={m.email_time}
                          onChange={(v) => set(module.id, { email_time: v })}
                          style={{ width: '100%', height: 40, fontSize: 15 }}
                        />
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      <button type="submit" className="btn btn-primary saveUserSettings" disabled={busy}>
        <i className="far fa-save"></i> Save
      </button>
    </form>
  )
}

/** Fila "Company reports" a randului desfacut (`companyReportsSettings/{user}`). */
function CompanyReportsTab({ userId }: { userId: number }) {
  const client = useQueryClient()
  const data = useQuery(userModulesQuery(userId))
  const [busy, setBusy] = useState(false)

  const initial = useMemo(() => {
    const out: Record<number, ModuleForm> = {}
    for (const row of data.data?.modules ?? []) out[row.module_id] = formOf(row)
    return out
  }, [data.data])

  if (data.error) return <PanelState error={data.error} onRetry={() => void data.refetch()} what="The company reports" />
  if (!data.data) return <Spinner />

  const modules = data.data.modules.map((row) => ({ id: row.module_id, name: row.display_name }))

  /* Capatul seamana cele cinci module la prima deschidere, deci gol inseamna
     ca firma chiar n-are niciun modul cu raport - nu ca a picat ceva. */
  if (modules.length === 0) return <PanelEmpty>No report modules for this user.</PanelEmpty>

  return (
    <ModulesForm
      modules={modules}
      initial={initial}
      busy={busy}
      onSave={(form) => {
        setBusy(true)
        void saveUserModules(userId, form)
          .then(() => {
            toast('Settings saved', 'Success')
            void client.invalidateQueries({ queryKey: ['settings', 'company-reports', 'users'] })
          })
          .catch((error: unknown) => toast(saveFailed(error), 'Error', 'error'))
          .finally(() => setBusy(false))
      }}
    />
  )
}

/** Fila "Other reports" a randului desfacut (`otherReportsSettings/{user}`). */
function OtherReportsTab({ userId }: { userId: number }) {
  const client = useQueryClient()
  const data = useQuery(userReportsQuery(userId))
  const [form, setForm] = useState<Record<number, { email: boolean; email_time: string }>>({})
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const out: Record<number, { email: boolean; email_time: string }> = {}
    for (const row of data.data?.reports ?? []) out[row.id] = { email: row.email === 1, email_time: row.email_time }
    setForm(out)
  }, [data.data])

  if (data.error) return <PanelState error={data.error} onRetry={() => void data.refetch()} what="The other reports" />
  if (!data.data) return <Spinner />

  const cell: React.CSSProperties = { textAlign: 'center', background: '#b3b3b3' }

  return (
    <>
      <div className="col-md-12 divider" style={{ marginTop: 0 }}>
        <h4 className="bordered">
          <strong>Enable report</strong> details
        </h4>
      </div>

      {data.data.reports.length === 0 ? (
        <PanelEmpty>No reports available.</PanelEmpty>
      ) : (
        <form
          className="form-horizontal labelcustomize userReports"
          onSubmit={(e) => {
            e.preventDefault()
            setBusy(true)
            void saveUserReports(userId, form)
              .then(() => {
                toast('Settings saved', 'Success')
                void client.invalidateQueries({ queryKey: ['settings', 'company-reports', 'users', userId, 'reports'] })
              })
              .catch((error: unknown) => toast(saveFailed(error), 'Error', 'error'))
              .finally(() => setBusy(false))
          }}
        >
          <table className="table table-hover">
            <thead>
              <tr>
                <th className="a-left" style={{ width: 200 }}>
                  Name
                </th>
                <th className="a-left" style={{ width: 200, textAlign: 'center' }}>
                  Email Report
                </th>
                <th className="a-left" style={{ width: 200, textAlign: 'center' }}>
                  Report time
                </th>
              </tr>
            </thead>
            <tbody>
              {data.data.reports.map((report) => {
                const r = form[report.id] ?? { email: false, email_time: report.email_time }
                return (
                  <tr key={report.id}>
                    <td className="a-left" scope="row">
                      <strong>{report.report_name}</strong>
                    </td>
                    <td style={cell} className="a-left">
                      <ICheck
                        color="blue"
                        label={`${report.report_name}: email report`}
                        checked={r.email}
                        onChange={(on) => setForm((prev) => ({ ...prev, [report.id]: { ...r, email: on } }))}
                      />
                    </td>
                    <td style={cell} className="a-left">
                      <TimeField
                        label={`${report.report_name}: report time`}
                        className="form-control"
                        value={r.email_time}
                        onChange={(v) => setForm((prev) => ({ ...prev, [report.id]: { ...r, email_time: v } }))}
                      />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          <button type="submit" className="btn btn-primary saveUserSettings" disabled={busy}>
            Save
          </button>
        </form>
      )}
    </>
  )
}

/** Randul desfacut: cele doua file din `actionTabs.php`. */
const ROW_TABS = [
  { id: 'company', label: 'Company reports' },
  { id: 'other', label: 'Other reports' },
] as const

export function UserReportsWorkspace({ userId }: { userId: number }) {
  const [tab, setTab] = useState<(typeof ROW_TABS)[number]['id']>('company')

  return (
    <div className="row d-flex m-0">
      <div className="col-auto bg-lightest-gray">
        <ul className="action_tabs">
          {ROW_TABS.map((t) => (
            <li key={t.id}>
              <a
                href={`/app/settings/company_reports/users_settings_report#${t.id}`}
                className={t.id === tab ? 'action_button active' : 'action_button'}
                onClick={(event: MouseEvent<HTMLAnchorElement>) => {
                  event.preventDefault()
                  setTab(t.id)
                }}
              >
                {t.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
      <div className="col-md-12">
        <div className="details_content">
          {tab === 'company' ? <CompanyReportsTab userId={userId} /> : <OtherReportsTab userId={userId} />}
        </div>
      </div>
    </div>
  )
}

/**
 * Widgetul de selectie multipla (`usersWidget.php`, mov): aratat cat timp e
 * bifat macar un rand; fila lui, "Company reports", e tabelul de module pentru
 * toti oamenii bifati (`multipleCompanyReportsSettings.php`).
 */
function SelectedUsersWidget({
  shown,
  ids,
  modules,
  onHide,
  onSaved,
}: {
  shown: boolean
  ids: number[]
  modules: ReportModule[]
  onHide(): void
  onSaved(): void
}) {
  const [busy, setBusy] = useState(false)
  const initial = useMemo(() => {
    const out: Record<number, ModuleForm> = {}
    for (const m of modules) out[m.id] = { ...EMPTY_FORM }
    return out
  }, [modules])

  return (
    <div className="multiple_check_widget widget border-mauve" style={{ display: shown ? undefined : 'none' }}>
      <div className="widget-header bg-mauve">
        <div className="widget-icon">
          <i className="fas fa-building"></i>
        </div>
        <h2 className="widget-title">Selected Users Settings</h2>
        <div className="widget-action">
          <button className="btn hide-widget" type="button" onClick={onHide}>
            <i className="fas fa-times"></i>
          </button>
        </div>
      </div>
      <div className="widget-content">
        <div style={{ width: '20%', display: 'inline-block', boxSizing: 'border-box' }}>
          <ul className="nav nav-metro-tabs tabs-left">
            <li data-class-nav="nav-content" data-toggle="tab" className="active">
              <a href="/app/settings/company_reports/users_settings_report#multiple" onClick={(e) => e.preventDefault()}>
                Company reports
              </a>
            </li>
          </ul>
        </div>
        <div
          className="nav-content"
          style={{ display: 'inline-block', width: '79%', boxSizing: 'border-box', verticalAlign: 'top', paddingLeft: 15 }}
        >
          {shown ? (
            <ModulesForm
              modules={modules.map((m) => ({ id: m.id, name: m.display_name }))}
              initial={initial}
              busy={busy}
              onSave={(form) => {
                setBusy(true)
                void saveManyUsersModules(ids, form)
                  .then(() => {
                    toast('Settings saved', 'Success')
                    onSaved()
                  })
                  .catch((error: unknown) => toast(saveFailed(error), 'Error', 'error'))
                  .finally(() => setBusy(false))
              }}
            />
          ) : null}
        </div>
      </div>
    </div>
  )
}

function UsersList({ shell }: { shell: UsersReportsShell }) {
  const client = useQueryClient()
  const [state, setState] = useTableState('settings-company-reports-users', {
    start: 0,
    length: 25,
    /* `order: [[1, 'asc']]` din `UsersTable.initDatatable`. */
    order: { column: 1, dir: 'asc' },
    search: '',
  })
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [widgetShown, setWidgetShown] = useState(false)
  const columns = useMemo(() => usersReportsColumns(), [])
  const table = useQuery({
    ...usersReportsTableQuery({
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: state.order.column,
      orderDir: state.order.dir,
      search: state.search,
    }),
    placeholderData: keepPreviousData,
  })

  /* Memorate: DataTable inchide randul desfacut cand primeste ALTA lista,
     deci lista nu se poate reface la fiecare desenare. */
  const raw = useMemo(() => (table.data?.data ?? []) as unknown as Record<string, unknown>[], [table.data])
  /* `__rid`: id-ul randului, socotit aici fiindca AICI se stie locul lui in
     lista. `rowId` primeste numai randul, deci doua randuri fara `ID` n-ar
     avea cum sa iasa deosebite din el. */
  const rows = useMemo(
    () => raw.map((r, i) => ({ ...r, __rid: rowIdOf(r, i), __checked: selected.has(userIdOf(r) ?? 0) })),
    [raw, selected]
  )

  /* Bifele sunt HTML scris de coloana, ca in DataTables: clicul se prinde deasupra tabelului. */
  const onClick = (e: MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement
    if (!(target instanceof HTMLInputElement)) return
    let next: Set<number> | null = null
    if (target.classList.contains('oneDevice')) {
      next = new Set(selected)
      if (target.checked) next.add(Number(target.value))
      else next.delete(Number(target.value))
    } else if (target.classList.contains('allDevices')) {
      /* "Toti" inseamna toti cei cu om, nu si randurile fara id. */
      next = target.checked ? new Set(raw.map(userIdOf).filter((id): id is number => id !== null)) : new Set()
    }
    if (next) {
      setSelected(next)
      /* `MultipleActions.widgetActions()`: se deschide la prima bifa. */
      if (next.size > 0) setWidgetShown(true)
    }
  }

  const ids = Array.from(selected)

  return (
    <>
      <SelectedUsersWidget
        shown={widgetShown && ids.length > 0}
        ids={ids}
        modules={shell.modules}
        onHide={() => setWidgetShown(false)}
        onSaved={() => void client.invalidateQueries({ queryKey: ['settings', 'company-reports', 'users'] })}
      />
      <div onClick={onClick}>
        <DataTable
          id="usersTable"
          columns={columns}
          rows={rows}
          total={table.data?.recordsFiltered ?? 0}
          fetching={table.isFetching}
          error={table.error ? failed(table.error) : null}
          onRetry={() => void table.refetch()}
          state={state}
          onState={setState}
          rowId={(row) => String(row.__rid)}
          emptyHint="No users here."
          details={(row) => {
            const id = userIdOf(row)

            return id === null ? <PanelNoId what="The report settings" /> : <UserReportsWorkspace userId={id} />
          }}
        />
      </div>
    </>
  )
}

export function UsersReportsPage() {
  const shell = useQuery(usersReportsShellQuery)

  if (shell.error instanceof ApiError && shell.error.status === 403) return <Forbidden />

  return (
    <CompanyReportsFrame current="users_settings">
      <div id="content">
        <header className="panel-heading" style={{ borderBottom: 0 }}>
          <h3>
            <strong>Users</strong> settings
          </h3>
          <label className="color">list of current users</label>
        </header>

        {!shell.data ? (
          shell.error ? (
            <div className="alert alert-danger">{failed(shell.error)}</div>
          ) : (
            <Spinner />
          )
        ) : shell.data.state === 'ok' ? (
          <UsersList shell={shell.data} />
        ) : shell.data.state === 'disabled' ? (
          <div className="alert alert-warning">
            <i className="fa fa-ban"></i> Reports on this company are disabled. In order to enable reports,{' '}
            <a href={shell.data.customersUrl} target="_blank" rel="noreferrer">
              click here.
            </a>
          </div>
        ) : (
          <div className="alert alert-danger">
            <i className="fa fa-ban"></i> Access denied.
          </div>
        )}
      </div>
    </CompanyReportsFrame>
  )
}
